﻿import React, { useState, useEffect } from 'react';
import { useNavigate, NavLink } from "react-router-dom";
import ProductLookupComponent from './Production/ProductLookupComponent';
import PackagingMaterialLookupComponent from './Production/PackagingMaterialLookupComponent';
import { CrudFunctions } from '../utilities/crud-functions';
import { ModalMessageType } from '../models/enums';
import { Composition } from '../models/composition';
import { Solvent } from '../models/solvent';
import { CompositionPackagingMaterial } from '../models/composition-packaging-material';
import { PackagingMaterial } from '../models/Production/packaging-material';
import { ProductListItem } from '../models/product-list-item';
import { CompositionsOtherMaterial } from '../models/Production/compositions-other-material';
import { Decimal4Format, Decimal7Format } from '../templates/pw-formatting';
import { ModalMetadata } from '../models/modal-metadata';
import GlobalDialog from './Modal/GlobalDialog';
import DmaFooter from './DmaFooter';

import DataGrid, {
    Column,
    Selection,
    Paging,
    Editing,
    Popup,
    Form,
    Scrolling,
    Summary,
    TotalItem,
    Toolbar,
    Item as ToolbarItem,
    Button as ColumnButton,
    LoadPanel,
    RowDragging
} from 'devextreme-react/data-grid';

import { NumberBox } from 'devextreme-react';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';
import { Item } from 'devextreme-react/form';
import { Toast } from 'devextreme-react/toast';

import { ToastProps } from '../models/toast-props';

let key = 0;

export interface Props {
    productId: string;
    catalogNumber: string;
    isReadOnly: boolean;
}

export default function ProductCompositionsData(props: Props) {
    const navigate = useNavigate();

    const [compositions, setCompositions] =
        useState<Composition[]>();

    const [solvents, setSolvents] =
        useState<Solvent[]>();

    const [packagingMaterials, setPackagingMaterials] =
        useState<CompositionPackagingMaterial[]>();

    const [otherMaterials, setOtherMaterials] =
        useState<CompositionsOtherMaterial[]>();

    const [currentElutionOrder, setCurrentElutionOrder] =
        useState<number>();

    const [newElutionOrder, setNewElutionOrder] =
        useState<number>();

    const [currentElutionCompositionId, setCurrentElutionCompositionId] =
        useState<string>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [changeElutionOrderPromptVisible, setChangeElutionOrderPromptVisible] =
        useState<boolean>();

    const [productLookupDialogVisible, setProductLookupDialogVisible] =
        useState<boolean>();

    const [solventLookupDialogVisible, setSolventLookupDialogVisible] =
        useState<boolean>();

    const [packagingMaterialsLookupDialogVisible, setPackagingMaterialsLookupDialogVisible] =
        useState<boolean>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    useEffect(() => {
        getAllData();
    }, [props.productId]);

    key++;

    return (
        <div id="product-raw-materials-datagrid-container" className="pw-margin-top-05">
            <div>
                <DataGrid
                    height="16rem"
                    wordWrapEnabled={true}
                    dataSource={compositions ? compositions : []}
                    rowAlternationEnabled={true}
                    onRowValidating={onCompositionsValidating}
                    onRowRemoving={onDeleteComposition}
                    showRowLines={true}
                    showBorders={true}
                    allowColumnResizing={true}
                    columnResizingMode='nextColumn'>

                    <LoadPanel enabled={true} />
                    <Selection mode='single' />
                    <Paging enabled={false} />

                    <Column type="buttons" width='4rem' >
                        <ColumnButton icon="edit" name="edit" />
                        <ColumnButton icon="trash" name="delete" />
                    </Column>

                    <Column dataField='elution' dataType="number" width='4rem'
                        alignment='center' />

                    <Column dataField='compositionCatalogNumber' dataType="string" width='7rem'
                        allowEditing={false} caption="Catalog#" />
                    <Column dataField='vendorPartNumber' dataType="string" width='8rem'
                        allowEditing={false} caption="Vendor Part#" />
                    <Column dataField='vendorPartName' dataType="string"
                        allowEditing={false} caption="Vendor Part Name" />
                    <Column dataField='purity' dataType="number"
                        format={Decimal4Format} width='6rem' />
                    <Column dataField='density' dataType="number"
                        format={Decimal4Format} width='6rem' />
                    <Column dataField='targetConcentrationPercent' caption="Target Conc. %" dataType="number"
                        format={Decimal4Format} width='6rem' />
                    <Column dataField='multiplier' caption="Multiplier" dataType="number"
                        format={Decimal7Format} width='6rem' />
                    <Column dataField='percentAnalyte' dataType="number" caption="% Analyte"
                        format={Decimal4Format} width='6rem' />
                    <Column dataField='percentConcentrationPpm' dataType="number" caption="Concentration ppm"
                        format={Decimal4Format} width='6rem' />

                    <RowDragging
                        allowReordering={true}
                        onReorder={onCompositionsReorder}
                        showDragIcons={false} />
                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Compositions</div>
                            </div>
                        </ToolbarItem>
                        <ToolbarItem location='after'>
                            <Button type='normal' text=""
                                hint='add composition' icon='plus' id="addCompositionToCompositionsButton"
                                onClick={addCompositionToCompositions} />
                        </ToolbarItem>
                    </Toolbar>

                    <Editing
                        mode="popup"
                        useIcons={false}
                        allowUpdating={!props.isReadOnly}
                        allowDeleting={!props.isReadOnly}>

                        <Popup title="Edit Composition" showTitle={true} width='80vw' height='80vh' />
                        <Form>
                            <Item dataField="elution" />
                            <Item dataField="purity" helpText="↳ reported as percentage, e.g. 100.0 for 100.0%" />
                            <Item dataField="density" />
                            <Item dataField="targetConcentrationPercent" helpText="↳ reported as percentage, e.g. 0.1 for 1000 ppm" />
                            <Item dataField="multiplier" />
                            <Item dataField="percentAnalyte" helpText="↳ reported as percentage, e.g. 100.0 for 100.0%" />
                            <Item dataField="percentConcentrationPpm" helpText="↳ reported as ppm, e.g. 1000 for 1000 ppm stock" />
                        </Form>

                    </Editing>

                </DataGrid>
            </div>

            <div className="pw-margin-top-1">
                {/* MATRIX DATAGRID */}
                <DataGrid
                    height="12rem"
                    wordWrapEnabled={true}
                    dataSource={solvents ? solvents : []}
                    onRowValidating={onSolventValidating}
                    onRowRemoving={onDeleteSolvent}
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    showBorders={true}
                    allowColumnResizing={true}
                    columnResizingMode='nextColumn'>

                    <LoadPanel enabled={true} />
                    <Selection mode={'single'} />
                    <Scrolling showScrollbar='always' />
                    <Paging enabled={false} />

                    <Column type="buttons" width='4rem' >
                        <ColumnButton name="edit" />
                        <ColumnButton name="delete" />
                    </Column>

                    <Column dataField='ratio' caption="% Ratio"
                        dataType="number" width='5rem' alignment='center' />

                    <Column dataField='compositionPartNumber' width="15rem" dataType="string" />
                    <Column dataField='vendorPartNumber' width="15rem" dataType="string" />
                    <Column dataField='vendorPartName' dataType="string" />

                    <Column dataField='targetWeight'
                        caption="Calculated Target Weight %"
                        dataType="number"
                        format={Decimal4Format}
                        width='12rem' />

                    <Summary>
                        <TotalItem column="ratio" summaryType="sum" />
                        <TotalItem column="targetWeight" summaryType="sum" valueFormat={Decimal4Format} />
                    </Summary>

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Matrix</div>
                            </div>
                        </ToolbarItem>
                        <ToolbarItem location='after'>
                            <Button type='normal' text=""
                                hint='add solvent' icon='plus' id="addSolventToSolventsButton"
                                onClick={addSolventToSolvents} />
                        </ToolbarItem>
                    </Toolbar>

                    <Editing
                        mode="popup"
                        useIcons={true}
                        allowUpdating={!props.isReadOnly}
                        allowDeleting={!props.isReadOnly}>

                        <Popup title="Solvent" showTitle={true} width={400} height={225} />
                        <Form>
                            <Item dataField="ratio" />
                        </Form>

                    </Editing>
                </DataGrid>
            </div>

            <div className="pw-margin-top-1">
                {/* PACKAGING MATERIAL DATAGRID */}
                <DataGrid
                    height="12rem"
                    keyExpr="compositionPackagingMaterialId"
                    wordWrapEnabled={true}
                    dataSource={packagingMaterials ? packagingMaterials : []}
                    onRowValidating={onPackagingMaterialRowValidating}
                    onRowRemoving={onDeletePackagingMaterial}
                    showBorders={true}
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    allowColumnResizing={true}
                    columnResizingMode='nextColumn'>

                    <LoadPanel enabled={true} />
                    <Selection mode={'single'} />
                    <Scrolling showScrollbar='always' />
                    <Paging enabled={false} />

                    <Column type="buttons" width='4rem' >
                        <ColumnButton name="edit" />
                        <ColumnButton name="delete" />
                    </Column>

                    <Column dataField='packagingName' width="15rem" dataType="string" />
                    <Column dataField='packagingDescription' dataType="string" />
                    <Column dataField='quantity' width="8rem" dataType="number" />

                    <Editing mode="popup" useIcons={true}
                        allowUpdating={!props.isReadOnly}
                        allowDeleting={!props.isReadOnly} />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Packaging Materials</div>
                            </div>
                        </ToolbarItem>
                        <ToolbarItem location='after'>
                            <NavLink to={'/packaging-materials-maintenance'} target="_blank" className="datagrid-icon-link">
                                <Button type='normal' text="Packaging Materials..."
                                    hint='Go to Packaging Materials Maintenance form' icon='bulletlist' id="goToPackagingMaterialsMaintenanceButton"
                                    width='12rem' />
                            </NavLink>
                        </ToolbarItem>
                        <ToolbarItem location='after'>
                            <Button type='normal' text=""
                                hint='add packaging material' icon='plus' id="addPackagingMaterialToCompositions"
                                onClick={addPackagingMaterialToCompositions} />
                        </ToolbarItem>
                    </Toolbar>

                    <Editing
                        mode="popup"
                        useIcons={true}
                        allowUpdating={true}
                        allowDeleting={!props.isReadOnly}>

                        <Popup title="Packaging Materials" showTitle={true} width="30rem" height="25rem" />
                        <Form>
                            <Item dataField="quantity" />
                        </Form>

                    </Editing>
                </DataGrid>
            </div>

            <div className="pw-margin-top-1">
                <DataGrid height="12rem"
                    dataSource={otherMaterials ? otherMaterials : []}
                    onRowValidating={onOtherMaterialRowValidating}
                    onRowRemoving={onOtherMaterialRowRemoving}
                    showBorders={true}
                    allowColumnResizing={true}
                    wordWrapEnabled={true}
                    keyExpr="otherMaterialId"
                    columnResizingMode="nextColumn"
                    rowAlternationEnabled={true}
                    showRowLines={true} >

                    <Paging enabled={false} />
                    <Selection mode='single' />

                    <Column type="buttons" width='7rem' >
                        <ColumnButton hint="edit standard" name="edit" />
                        <ColumnButton hint="remove standard" name="delete" />
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={onOtherMaterialAuditButtonClicked} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onOtherMaterialInformationButtonClicked} />
                    </Column>

                    <Column dataField='name' width='10rem' dataType="string" />
                    <Column dataField='partNumber' caption="Part #" width="10rem" dataType="string" />
                    <Column dataField='quantity' width='10rem' dataType="number" />
                    <Column dataField='uom' caption="UOM" width="6rem" dataType="string" />
                    <Column dataField='description' dataType="string" />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Other Materials</div>
                            </div>
                        </ToolbarItem>
                        <ToolbarItem location='after' name="addRowButton" />

                    </Toolbar>

                    <Editing
                        mode="popup"
                        useIcons={true}
                        allowAdding={!props.isReadOnly}
                        allowUpdating={!props.isReadOnly}
                        allowDeleting={!props.isReadOnly}>

                        <Popup title="Edit Other Material" showTitle={true} width='80vw' height='80vh' />
                        <Form>
                            <Item dataField="name" />
                            <Item dataField="partNumber" />
                            <Item dataField="quantity" />
                            <Item dataField="uom" />
                            <Item dataField="description" colSpan={2} />
                        </Form>

                    </Editing>
                </DataGrid>

            </div>

            <DmaFooter />

            {changeElutionOrderPromptVisible &&
                <DialogPopup
                    visible={changeElutionOrderPromptVisible}
                    onHiding={hideChangeElutionOrderPrompt}
                    dragEnabled={false}
                    hideOnOutsideClick={false}
                    showTitle={true}
                    title="Change Elution Order"
                    width={400}
                    height={200}>

                    <div>
                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-10">
                                <div className="pw-form-unit-label">Current Elution Order</div>
                                <div className="pw-readonly-label">{currentElutionOrder}</div>
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-10">
                                <div className="pw-form-unit-label">New Elution Order</div>
                                <NumberBox id="txtChangeElutionOrder"
                                    className="textbox"
                                    onValueChanged={newElutionOrderChanged}
                                    placeholder="batch name" value={newElutionOrder} />
                            </div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-centered-buttons-container">
                            <Button type="success" id="changeElutionOrderButton" className="pw-button" onClick={changeElutionOrder} text="Save" />
                            <Button className="pw-button" id="hideChangeElutionOrderButton" onClick={hideChangeElutionOrderPrompt} text="Cancel" />
                        </div>
                    </div>

                </DialogPopup>
            }

            {productLookupDialogVisible &&
                <DialogPopup
                    visible={productLookupDialogVisible}
                    onHiding={hideProductLookupDialog}
                    showTitle={true}
                    fullScreen={true}
                    title="Compositions Lookup">
                    <ProductLookupComponent onApplySelections={addCompositionsLookupDialogApplySelection}
                        onClose={onProductLookupDialogClose} />
                </DialogPopup>
            }

            {solventLookupDialogVisible &&
                <DialogPopup
                    visible={solventLookupDialogVisible}
                    onHiding={hideSolventLookupDialog}
                    showTitle={true}
                    fullScreen={true}
                    title="Solvents Lookup">
                    <ProductLookupComponent onApplySelections={onSolventLookupDialogApplySelection}
                        onClose={onSolventLookupDialogCancel} />
                </DialogPopup>
            }

            {packagingMaterialsLookupDialogVisible &&
                <DialogPopup
                    visible={packagingMaterialsLookupDialogVisible}
                    onHiding={hidePackagingMaterialsLookupDialog}
                    showTitle={true}
                    title="Packaging Materials Lookup"
                    width='80vw'
                    height='80vh'>
                    <PackagingMaterialLookupComponent onApplySelection={onPackagingMaterialsLookupDialogApplySelection}
                        onCancel={onPackagingMaterialsLookupDialogCancel} />
                </DialogPopup>
            }

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function onCompositionsReorder(e: any) {
        //console.log("onCompositionsReorder");
        //console.log(e);

        const visibleRows = e.component.getVisibleRows();
        const newCompositions = [...compositions];

        const toIndex = newCompositions.findIndex(c => c.compositionId === visibleRows[e.toIndex].data.compositionId);
        const fromIndex = newCompositions.findIndex(c => c.compositionId === e.itemData.compositionId);

        newCompositions.splice(fromIndex, 1);
        newCompositions.splice(toIndex, 0, e.itemData);

        setCompositions(newCompositions);

        //todo - update elutions in db

    }

    function addCompositionToCompositions() {
        showProductLookupDialog();
    }

    function addSolventToSolvents() {
        showSolventLookupDialog();
    }

    function onProductLookupDialogClose() {
        hideProductLookupDialog();
        getCompositions();
    }

    async function addCompositionsLookupDialogApplySelection(products: ProductListItem[]) {
        //console.log("addProductToSelectedProducts");
        //console.log(e);

        if (products && products.length > 0) {
            let addedCompositions: Composition[] = [];

            let maxElution = Math.max(...compositions.map(c => c.elution));

            let elutionIdx = maxElution ?? 0;

            products.forEach(p => {
                elutionIdx++;

                addedCompositions.push(
                    {
                        elution: elutionIdx,
                        productId: props.productId,
                        compositionProductId: p.productId
                    });
            })

            await insertCompositions(addedCompositions)
                .then(() => {
                    getCompositions();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Adding Compositions");
                });
        }
    }

    async function onSolventLookupDialogApplySelection(products: ProductListItem[]) {
        if (products && products.length > 0) {
            let addedSolvents: Composition[] = [];

            products.forEach(s => {
                addedSolvents.push(
                    {
                        elution: 0,
                        productId: props.productId,
                        compositionProductId: s.productId
                    });
            })

            if (addedSolvents && addedSolvents.length > 0) {

                await insertSolvents(addedSolvents)
                    .then(() => {
                        getSolvents();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Inserting Solvents");
                    });
            }
        }

        hideSolventLookupDialog();
    }

    function onSolventLookupDialogCancel() {
        hideSolventLookupDialog();
    }

    async function onPackagingMaterialsLookupDialogApplySelection(packagingMaterials: PackagingMaterial[]) {
        if (packagingMaterials && packagingMaterials.length > 0) {

            let compositionPackagingMaterials: CompositionPackagingMaterial[] = [];

            packagingMaterials.forEach(pm => {
                compositionPackagingMaterials.push(
                    {
                        packagingMaterialId: pm.packagingMaterialId,
                        productId: props.productId
                    });
            })

            await insertPackagingMaterials(compositionPackagingMaterials)
                .then(() => {
                    getPackagingMaterials();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Inserting Packaging Materials");
                });
        }

        hidePackagingMaterialsLookupDialog();
    }

    function onPackagingMaterialsLookupDialogCancel() {
        hidePackagingMaterialsLookupDialog();
    }

    function addPackagingMaterialToCompositions() {
        showPackagingMaterialsLookupDialog();
    }

    function newElutionOrderChanged(e: any) {
        setNewElutionOrder(e.value);
    }

    function changeElutionOrder() {
        console.log(currentElutionCompositionId);
        console.log(currentElutionOrder);
        console.log(newElutionOrder);

        hideChangeElutionOrderPrompt();
    }

    async function onCompositionsValidating(e: any) {
        if (e.oldData) {
            let composition = e.oldData as Composition;

            if (composition) {
                let updatedComposition = { ...composition, ...e.newData };
                updateComposition(updatedComposition)
                    .then(() => {
                        getCompositions();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Composition");
                    });
            }
        }
    }

    async function onSolventValidating(e: any) {
        if (e.oldData) {
            let solvent = e.oldData as Solvent;

            if (solvent) {
                let updatedSolvent = { ...solvent, ...e.newData };
                updateSolvent(updatedSolvent)
                    .then(() => {
                        getSolvents();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Solvent");
                    });
            }
        }
    }

    async function onPackagingMaterialRowValidating(e: any) {
        if (e.oldData) {
            let packagingMaterial = e.oldData as CompositionPackagingMaterial;

            if (packagingMaterial) {
                let updatedPackagingMaterial = { ...packagingMaterial, ...e.newData };
                updatePackagingMaterial(updatedPackagingMaterial)
                    .then(() => {
                        getPackagingMaterials();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Packaging Material");
                    });
            }
        }
    }

    async function onOtherMaterialRowValidating(e: any) {
        if (e.oldData) {
            let otherMaterial = e.oldData as CompositionsOtherMaterial;

            if (otherMaterial) {
                let updatedOtherMaterial = { ...otherMaterial, ...e.newData };
                updateOtherMaterial(updatedOtherMaterial)
                    .then(() => {
                        getOtherMaterials();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Other Material");
                    });
            }
        }
        else if (e.newData) {
            let otherMaterial = e.newData as CompositionsOtherMaterial;

            otherMaterial.productId = props.productId;

            await insertOtherMaterial(otherMaterial)
                .then(() => {
                    getOtherMaterials();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Inserting Other Material");
                });
        }
    }

    async function onOtherMaterialRowRemoving(e: any) {
        if (e.data && e.data.otherMaterialId) {
            await deleteOtherMaterial(e.data.otherMaterialId)
                .then(() => {
                    getOtherMaterials();

                    //todo add Toast
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Other Material");
                });
        }
    }

    async function onDeleteComposition(e: any) {
        if (e.data && e.data.compositionId) {
            await deleteComposition(e.data.compositionId)
                .then(() => {
                    getCompositions();

                    //todo add Toast
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Composition");
                });
        }
    }

    async function onDeleteSolvent(e: any) {
       //the solventId here points to the compositionId in the database
        //  (the Compositions table holds both the compositions and the solvents: Solvents have an elution = 0)
        if (e.data && e.data.solventId) {
            await deleteComposition(e.data.solventId)
                .then(() => {
                    getSolvents();

                    //todo add Toast
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Solvent");
                });
        }
    }

    async function onDeletePackagingMaterial(e: any) {
       e.data.compositionPackagingMaterialId

        if (e.data && e.data.compositionPackagingMaterialId) {
            await deletePackagingMaterial(e.data.compositionPackagingMaterialId)
                .then(() => {
                    getPackagingMaterials();

                    //todo add Toast
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Packaging Material");
                });
        }
    }

    // GET functions

    function getCompositions() {
        return CrudFunctions.getData(`products/compositionsbyproductid/${props.productId}`)
            .then(data => {
                setCompositions(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Compositions");
            });
    }

    function getSolvents() {
        return CrudFunctions.getData(`products/solventsbyproductid/${props.productId}`)
            .then(data => {
                setSolvents(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Solvents");
            });
    }

    function getPackagingMaterials() {
        return CrudFunctions.getData(`products/compositionpackagingmaterialsbyproductid/${props.productId}`)
            .then(data => {
                setPackagingMaterials(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Packaging Materials");
            });
    }

    function getOtherMaterials() {
        return CrudFunctions.getData(`products/compositionsothermaterials/${props.productId}`)
            .then(data => {
                setOtherMaterials(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Other Materials");
            });
    }

    //POST functions

    async function insertCompositions(compositions: Composition[]) {
        return CrudFunctions.postData("products/compositions", compositions);
    }

    async function insertSolvents(solvents: Composition[]) {
        return CrudFunctions.postData("products/solvents", solvents);
    }

    async function insertPackagingMaterials(packagingMaterials: CompositionPackagingMaterial[]) {
        return CrudFunctions.postData("products/compositionpackagingmaterials", packagingMaterials);
    }

    async function insertOtherMaterial(otherMaterial: CompositionsOtherMaterial) {
        return CrudFunctions.postData("products/compositionsothermaterial", otherMaterial);
    }

    //PUT functions

    async function updateComposition(composition: Composition) {
        return CrudFunctions.putData("products/composition", composition);
    }

    async function updateSolvent(solvent: Composition) {
        return CrudFunctions.putData("products/solvent", solvent);
    }

    async function updatePackagingMaterial(packagingMaterial: CompositionPackagingMaterial) {
        return CrudFunctions.putData("products/compositionpackagingmaterial", packagingMaterial);
    }

    async function updateOtherMaterial(otherMaterial: CompositionsOtherMaterial) {
        return CrudFunctions.putData("products/compositionsothermaterial", otherMaterial);
    }

    // DELETE functions

    async function deleteComposition(compositionId: string) {
        return CrudFunctions.deleteData(`products/composition/${compositionId}`)
    }

    async function deletePackagingMaterial(compositionPackagingMaterialId: string) {
        return CrudFunctions.deleteData(`products/compositionpackagingmaterial/${compositionPackagingMaterialId}`)
    }

    async function deleteOtherMaterial(otherMaterialId: string) {
        return CrudFunctions.deleteData(`products/compositionsothermaterial/${otherMaterialId}`)
    }

    // Show/Hide
    function hideChangeElutionOrderPrompt() {
        setChangeElutionOrderPromptVisible(false);
    }

    function showProductLookupDialog() {
        setProductLookupDialogVisible(true);
    }

    function hideProductLookupDialog() {
        setProductLookupDialogVisible(false);
    }

    function showSolventLookupDialog() {
        setSolventLookupDialogVisible(true);
    }

    function hideSolventLookupDialog() {
        setSolventLookupDialogVisible(false);
    }

    function showPackagingMaterialsLookupDialog() {
        setPackagingMaterialsLookupDialogVisible(true);
    }

    function hidePackagingMaterialsLookupDialog() {
        setPackagingMaterialsLookupDialogVisible(false);
    }

    function onOtherMaterialAuditButtonClicked(e: any) {

    }

    function onOtherMaterialInformationButtonClicked(e: any) {

    }

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    //other

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    async function getAllData() {
        await Promise.all([
            getCompositions(),
            getSolvents(),
            getPackagingMaterials(),
            getOtherMaterials()
        ]);
    }
}